import { ActionIcon, Badge, Card, Group, Stack, Text } from '@mantine/core';
import { IconCheck, IconTrash } from '@tabler/icons-react';
import { Topic } from '../../types/TopicModels';
import { primaryColor } from '../../constants/theme';

export interface TopicCardProps {
  topic: Topic;
  isSelected?: boolean;
  isEditMode?: boolean;
  hideDescription?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  variant?: 'grid' | 'row';
}

const getTopicRateLabel = (rate?: number) => {
  if (!rate || rate === 0) {
    return 'Free';
  }

  return `£${rate}`;
};

export const TopicCard = ({
  topic,
  isSelected,
  isEditMode,
  hideDescription,
  onClick,
  onDelete,
  variant = 'grid',
}: TopicCardProps) => {
  const color = topic.colour ?? '#5ECFB1';

  if (variant === 'row') {
    return (
      <Card
        withBorder
        radius="md"
        p={0}
        style={{
          borderColor: isSelected ? color : undefined,
          cursor: onClick ? 'pointer' : 'default',
        }}
        onClick={onClick}
      >
        <Group align="stretch" gap={0} wrap="nowrap">
          <div style={{ width: 4, background: color }} />
          <Stack p="sm" gap={6} align="flex-start" style={{ flex: 1 }}>
            <Group justify="space-between" align="start">
              <Text fw={600} ta="left">{topic.title || 'Ask Me Anything'}</Text>
              {isEditMode && onDelete && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete();
                  }}
                  aria-label="Delete topic"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </Group>

            {!hideDescription && topic.description && (
              <Text size="sm" c="dimmed" lineClamp={2} ta="left">
                {topic.description}
              </Text>
            )}

            <Group gap={8}>
              <Badge radius="sm" color="gray" variant="light">
                {topic.durationMinutes ?? 30} mins
              </Badge>
              <Badge radius="sm" color={primaryColor} variant="light">
                {getTopicRateLabel(topic.rate)}
              </Badge>
              <Badge radius="sm" color={topic.isActive ? 'teal' : 'gray'} variant="light">
                {topic.isActive ? 'Active' : 'Paused'}
              </Badge>
            </Group>
          </Stack>
        </Group>
      </Card>
    );
  }

  return (
    <Card
      withBorder
      radius="md"
      p={0}
      shadow="none"
      style={{
        cursor: onClick ? 'pointer' : 'default',
        borderColor: isSelected ? color : undefined,
        borderWidth: isSelected ? 2 : 1,
        boxShadow: 'none',
      }}
      onClick={onClick}
    >
      <Stack gap={0}>
        <div
          style={{
            background: color,
            minHeight: 120,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            position: 'relative',
            padding: 16,
            color: 'white',
          }}
        >
          <Text fw={700} size="md" lineClamp={2} ta="left">
            {topic.title || 'Ask Me Anything'}
          </Text>
          {topic.description && (
            <Text size="xs" c="white" ta="left" lineClamp={3} mt={4} style={{ opacity: 0.85 }}>
              {topic.description}
            </Text>
          )}
          {isSelected && (
            <IconCheck
              size={20}
              style={{ position: 'absolute', top: 12, right: 12 }}
            />
          )}
        </div>

        <Group justify="space-between" p="sm">
          <Text c="dimmed" size="sm">
            {topic.durationMinutes ?? 30} mins
          </Text>
          <Text fw={600}>{getTopicRateLabel(topic.rate)}</Text>
        </Group>
      </Stack>
    </Card>
  );
};
